'use client';

/**
 * Non-Turnstile counterpart to the gated Beacon+TurnstileComponent tree.
 * Mounts Beacon (html_rendered / js_painted) and BounceTracker (bounced)
 * tagged with `turnstile_enabled=false`, alongside the normal
 * RedirectComponent that drives navigation. Used for every vertical's
 * non-gated render path, not banking-specific — dashboards split by
 * `product_type` to compare conversion across verticals.
 *
 * Lives in turnstile-component/ (rather than shared-components/) because
 * it is purely an implementation detail of TurnstileGate. The boundary
 * with the rest of the redirect tree is: handler → TurnstileGate →
 * (this | gated tree | suppressed tree). Anything else mounting
 * Beacon/BounceTracker by hand is a code smell — go through the gate.
 */
import { useRef } from 'react';

import Beacon from '../beacon';
import BounceTracker from '../bounce-tracker';
import RedirectComponent from '../redirect-component';
import { RedirectContext } from '../redirect-component/redirectContext';

interface Props {
  redirectContext: RedirectContext;
}

export default function UngatedRedirectBeacons({
  redirectContext,
}: Props): JSX.Element {
  // Flipped from inside RedirectComponent the moment we commit to
  // navigating. A pagehide after this point is a natural unload, not a
  // bounce — the predicate below returns null and the beacon stays
  // suppressed. Using a ref (not state) avoids an extra re-render and
  // lets BounceTracker observe the latest value directly.
  const hasInitiatedNavigationRef = useRef(false);

  const getBounceParams = (): Record<string, string> | null => {
    if (hasInitiatedNavigationRef.current) return null;
    return {
      pt: redirectContext.productType || 'unknown',
      turnstile_enabled: 'false',
    };
  };

  return (
    <>
      <Beacon
        productType={redirectContext.productType}
        turnstileEnabled={false}
      />
      <BounceTracker getBeaconParams={getBounceParams} />
      <RedirectComponent
        redirectContext={redirectContext}
        onRedirectInitiated={() => {
          hasInitiatedNavigationRef.current = true;
        }}
      />
    </>
  );
}
